import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import {
  BrowserProvider,
  Eip1193Provider,
  formatEther,
  JsonRpcSigner,
} from 'ethers';
import { JoyEnvironment } from '../../environments/environment';
import { JOY_ENVIRONMENT } from '../app.config';
import { WalletType } from '../models/joy.types';

/**
 * Public Chainlist RPC endpoints for wallet_addEthereumChain.
 * Per EIP-3085 security guidance: use well-known public nodes,
 * NOT the platform's own RPC, so users can verify the endpoints.
 */
const CHAIN_ADD_CONFIGS: Record<
  number,
  {
    chainName: string;
    nativeCurrency: { name: string; symbol: string; decimals: number };
    rpcUrls: string[];
    blockExplorerUrls: string[];
  }
> = {
  56: {
    chainName: 'BNB Smart Chain',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: [
      'https://bsc-dataseed.binance.org',
      'https://bsc-dataseed1.defibit.io',
      'https://bsc.publicnode.com',
    ],
    blockExplorerUrls: ['https://bscscan.com'],
  },
  80002: {
    chainName: 'Polygon Amoy Testnet',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
    rpcUrls: [
      'https://rpc-amoy.polygon.technology/',
      'https://polygon-amoy.drpc.org',
    ],
    blockExplorerUrls: ['https://amoy.polygonscan.com'],
  },
  42161: {
    chainName: 'Arbitrum One',
    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
    rpcUrls: [
      'https://arb1.arbitrum.io/rpc',
      'https://arbitrum-one.publicnode.com',
    ],
    blockExplorerUrls: ['https://arbiscan.io'],
  },
};

function getEthereum(): Eip1193Provider | undefined {
  return (globalThis as Record<string, unknown>)['ethereum'] as
    | Eip1193Provider
    | undefined;
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (Safari private, SSR, quota exceeded)
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable
  }
}

@Injectable({ providedIn: 'root' })
export class WalletService {
  readonly address = signal<string | null>(null);
  readonly chainId = signal<number | null>(null);
  readonly isConnected = computed(() => this.address() !== null);
  readonly shortAddress = computed(() => {
    const addr = this.address();
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  });
  readonly isCorrectChain = computed(() => this.chainId() === this.env.chainId);

  readonly referrerAddress = signal<string | null>(null);

  readonly walletType = signal<WalletType | null>(null);
  readonly isConnecting = signal(false);
  readonly balance = signal<bigint | null>(null);
  readonly balanceFormatted = computed(() => {
    const b = this.balance();
    return b !== null ? formatEther(b) : '0';
  });

  /** Bumped after any contract write op — TokenBalanceService tracks this to re-fetch */
  readonly balanceVersion = signal(0);

  private provider: BrowserProvider | null = null;
  private wcProvider: InstanceType<typeof EthereumProvider> | null = null;
  private eventTarget: EthereumEventTarget | null = null;
  private readonly zone = inject(NgZone);
  private readonly env = inject<JoyEnvironment>(JOY_ENVIRONMENT);

  private onAccountsChanged = (accounts: string[]) => {
    this.zone.run(() => {
      if (accounts.length === 0) {
        this.disconnect();
      } else if (this.isValidAddress(accounts[0])) {
        this.address.set(accounts[0]);
        this.refreshBalance();
      }
    });
  };

  private onChainChanged = (chainIdHex: string) => {
    this.zone.run(() => {
      this.chainId.set(Number(chainIdHex));
    });
  };

  private onDisconnect = () => {
    this.zone.run(() => {
      this.disconnect();
    });
  };

  /** True when contracts are not deployed — full mock mode */
  private get isDevMock(): boolean {
    return !this.env.isProduction && !this.env.contracts.loan;
  }

  constructor() {
    // Restore referrer from localStorage (validate before trusting)
    const savedReferrer = safeGetItem('joy_referrer_address');
    if (savedReferrer && this.isValidAddress(savedReferrer)) {
      this.referrerAddress.set(savedReferrer);
    }

    // Dev mock mode — auto-connect without real wallet
    if (this.isDevMock) {
      this.address.set('0xDEV0000000000000000000000000000000000001');
      this.chainId.set(this.env.chainId);
      this.balance.set(5000000000000000000n); // 5 BNB
      this.walletType.set('metamask');
      // Auto-set referrer so binding modal is bypassed
      if (!this.referrerAddress()) {
        this.referrerAddress.set(this.env.platformAddress);
      }
      return;
    }

    // E2E testing bridge — only in non-production
    if (!this.env.isProduction) {
      const e2eAddr = safeGetItem('e2e_wallet_address');
      if (e2eAddr && this.isValidAddress(e2eAddr)) {
        this.address.set(e2eAddr);
        this.chainId.set(Number(safeGetItem('e2e_wallet_chainId') || '56'));
        return;
      }
    }
    this.tryReconnect();
  }

  get platformAddress(): string {
    return this.env.platformAddress;
  }

  setReferrer(address: string): void {
    if (!this.isValidAddress(address)) return;
    this.referrerAddress.set(address);
    safeSetItem('joy_referrer_address', address);
  }

  isValidAddress(address: string): boolean {
    return /^0x[0-9a-fA-F]{40}$/.test(address);
  }

  async connect(type: WalletType): Promise<void> {
    this.isConnecting.set(true);
    try {
      if (type === 'metamask' || type === 'trustwallet') {
        await this.connectBrowser();
      } else {
        await this.connectWalletConnect();
      }
      this.walletType.set(type);
      safeSetItem('joy_wallet_type', type);
    } catch (err) {
      console.error('[WalletService] connect error:', err);
      throw err;
    } finally {
      this.isConnecting.set(false);
    }
  }

  private async connectBrowser(): Promise<void> {
    const ethereum = getEthereum();
    if (!ethereum) {
      throw new Error('No browser wallet detected');
    }
    this.provider = new BrowserProvider(ethereum);
    try {
      await this.provider.send('eth_requestAccounts', []);
    } catch (err: unknown) {
      const rpcErr = err as { code?: number; error?: { code?: number } };
      // 4001: user rejected — surface friendly message
      if (rpcErr?.code === 4001 || rpcErr?.error?.code === 4001) {
        throw new Error('User rejected connection');
      }
      // -32002: already pending — poll eth_accounts until user approves
      if (rpcErr?.code === -32002 || rpcErr?.error?.code === -32002) {
        const accounts = await this.pollAccounts();
        if (accounts.length === 0) throw new Error('User rejected connection');
        if (!this.isValidAddress(accounts[0]))
          throw new Error('Invalid address from wallet');
        this.address.set(accounts[0]);
        const network = await this.provider.getNetwork();
        this.chainId.set(Number(network.chainId));
        await this.refreshBalance();
        this.listenEvents();
        return;
      }
      throw err;
    }
    const signer = await this.provider.getSigner();
    this.address.set(await signer.getAddress());
    const network = await this.provider.getNetwork();
    this.chainId.set(Number(network.chainId));
    await this.refreshBalance();
    this.listenEvents();
  }

  private pollAccounts(): Promise<string[]> {
    return new Promise(resolve => {
      let resolved = false;
      const interval = setInterval(async () => {
        if (!this.provider || resolved) {
          clearInterval(interval);
          if (!resolved) {
            resolved = true;
            resolve([]);
          }
          return;
        }
        try {
          const accounts: string[] = await this.provider.send(
            'eth_accounts',
            [],
          );
          if (accounts.length > 0) {
            clearInterval(interval);
            if (!resolved) {
              resolved = true;
              resolve(accounts);
            }
          }
        } catch {
          // RPC error during polling — continue retrying
        }
      }, 500);
      // Timeout after 120s
      const timeout = setTimeout(() => {
        clearInterval(interval);
        if (!resolved) {
          resolved = true;
          resolve([]);
        }
      }, 120_000);
      // Clean up timeout if resolved early
      const origResolve = resolve;
      resolve = val => {
        clearTimeout(timeout);
        origResolve(val);
      };
    });
  }

  private async connectWalletConnect(): Promise<void> {
    // Run WalletConnect init + modal outside Angular zone.
    // WalletConnect's QR modal creates/destroys shadow DOM elements;
    // zone.js intercepts those operations and triggers change detection,
    // causing "removeChild: not a child of this node" errors.
    const wcProvider = await this.zone.runOutsideAngular(async () => {
      const p = await EthereumProvider.init({
        projectId: this.env.walletConnectProjectId,
        chains: [this.env.chainId],
        showQrModal: true,
      });
      await p.enable();
      return p;
    });
    this.wcProvider = wcProvider;
    this.provider = new BrowserProvider(this.wcProvider);
    const signer = await this.provider.getSigner();
    this.address.set(await signer.getAddress());
    const network = await this.provider.getNetwork();
    this.chainId.set(Number(network.chainId));
    await this.refreshBalance();
    this.listenEvents();
  }

  disconnect(): void {
    this.address.set(null);
    this.chainId.set(null);
    this.walletType.set(null);
    this.isConnecting.set(false);
    this.balance.set(null);
    this.referrerAddress.set(null);
    safeRemoveItem('joy_referrer_address');
    safeRemoveItem('joy_wallet_type');
    this.removeListeners();
    if (this.wcProvider) {
      const wc = this.wcProvider;
      this.wcProvider = null;
      this.zone.runOutsideAngular(() => void wc.disconnect());
    }
    this.provider = null;
  }

  async switchChain(): Promise<void> {
    if (!this.provider) throw new Error('Wallet not connected');
    const hexChainId = '0x' + this.env.chainId.toString(16);
    try {
      // Step 1: Try switching to chain (MetaMask official pattern)
      await this.provider.send('wallet_switchEthereumChain', [
        { chainId: hexChainId },
      ]);
    } catch (err: unknown) {
      const rpcErr = err as { code?: number };
      // 4902 = chain not added. -32603 = MetaMask Mobile returns this instead of 4902
      if (rpcErr?.code === -32603) {
        console.warn(
          '[WalletService] switchChain got -32603 (MetaMask Mobile), treating as chain-not-added',
        );
      }
      if (rpcErr?.code === 4902 || rpcErr?.code === -32603) {
        // Step 2: Chain not found — add it with public Chainlist RPC nodes
        // Per EIP-3085: use well-known public endpoints, not platform's own RPC
        const config = CHAIN_ADD_CONFIGS[this.env.chainId];
        if (!config) {
          throw new Error(
            `No chain configuration for chainId ${this.env.chainId}`,
          );
        }
        try {
          await this.provider.send('wallet_addEthereumChain', [
            {
              chainId: hexChainId,
              chainName: config.chainName,
              nativeCurrency: config.nativeCurrency,
              rpcUrls: config.rpcUrls,
              blockExplorerUrls: config.blockExplorerUrls,
            },
          ]);
        } catch (addErr: unknown) {
          const addRpcErr = addErr as { code?: number };
          if (addRpcErr?.code === 4001) {
            throw new Error('User rejected adding the network');
          }
          throw new Error(
            'Failed to add network — please add it manually in your wallet',
          );
        }
        // Step 3: MetaMask auto-switches after add, but per EIP-3085 spec
        // auto-switch is not guaranteed. Explicitly switch for cross-wallet compat.
        if (!this.isCorrectChain()) {
          await this.provider.send('wallet_switchEthereumChain', [
            { chainId: hexChainId },
          ]);
        }
      } else {
        throw err;
      }
    }
    // Sync chainId immediately — don't wait for async chainChanged event
    this.chainId.set(this.env.chainId);
  }

  async getSigner(): Promise<JsonRpcSigner> {
    if (!this.provider) throw new Error('Wallet not connected');
    return this.provider.getSigner();
  }

  async tryReconnect(): Promise<void> {
    const savedType = safeGetItem('joy_wallet_type') as WalletType | null;
    if (!savedType) return;

    try {
      if (savedType === 'metamask' || savedType === 'trustwallet') {
        // Silent reconnect — eth_accounts doesn't trigger popup
        const ethereum = getEthereum();
        if (!ethereum) return;
        this.provider = new BrowserProvider(ethereum);
        const accounts: string[] = await this.provider.send('eth_accounts', []);
        if (accounts.length === 0 || !this.isValidAddress(accounts[0])) {
          safeRemoveItem('joy_wallet_type');
          return;
        }
        this.address.set(accounts[0]);
        const network = await this.provider.getNetwork();
        this.chainId.set(Number(network.chainId));
        this.walletType.set(savedType);
        this.listenEvents();
        await this.refreshBalance();
      } else {
        await this.connect(savedType);
      }
    } catch (err) {
      console.warn('[WalletService] tryReconnect failed:', err);
      safeRemoveItem('joy_wallet_type');
    }
  }

  async refreshBalance(): Promise<void> {
    if (!this.isDevMock) {
      const addr = this.address();
      if (this.provider && addr) {
        try {
          const bal = await this.provider.getBalance(addr);
          if (this.address() === addr) {
            this.balance.set(bal);
          }
        } catch (err) {
          console.warn('[WalletService] refreshBalance failed:', err);
        }
      }
    }
    // Always notify — TokenBalanceService needs to re-fetch JOY balance even in dev mock
    this.balanceVersion.update(v => v + 1);
  }

  private listenEvents(): void {
    const target = (this.wcProvider ?? getEthereum()) as
      | EthereumEventTarget
      | undefined;
    if (!target) return;
    // Store reference for proper cleanup
    this.eventTarget = target;
    target.on('accountsChanged', this.onAccountsChanged);
    target.on('chainChanged', this.onChainChanged);
    target.on('disconnect', this.onDisconnect);
  }

  private removeListeners(): void {
    const target = this.eventTarget;
    if (!target) return;
    target.removeListener('accountsChanged', this.onAccountsChanged);
    target.removeListener('chainChanged', this.onChainChanged);
    target.removeListener('disconnect', this.onDisconnect);
    this.eventTarget = null;
  }
}

interface EthereumEventTarget {
  on(event: 'accountsChanged', handler: (accounts: string[]) => void): void;
  on(event: 'chainChanged', handler: (chainId: string) => void): void;
  on(event: 'disconnect', handler: () => void): void;
  removeListener(
    event: 'accountsChanged',
    handler: (accounts: string[]) => void,
  ): void;
  removeListener(
    event: 'chainChanged',
    handler: (chainId: string) => void,
  ): void;
  removeListener(event: 'disconnect', handler: () => void): void;
}
